// ModalConfirmacaoCode.jsx
import React, { useEffect, useRef, useState } from 'react'
import * as S from './styles'
import closeIcon from '../../assets/icons/icon-close.svg'
import { useToast } from '../../hooks/toast'
import clientes from '../../api/clientes'
import Loader from '../Loader'

const ModalConfirmacaoCode = ({
  onClose,
  onConfirm,
  userEmail,
  novoEmail,
  setCodigoConfirmado
}) => {
  const { addToast } = useToast()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const inputsRef = useRef([])
  const [status, setStatus] = useState('idle')
  const [canResend, setCanResend] = useState(false)
  const [timeLeft, setTimeLeft] = useState(300) // 5 minutos
  const [loading, setLoading] = useState(false)
  const intervalRef = useRef(null)

  const maskEmail = (email) => {
    const [user, domain] = email.split('@')
    if (!user || !domain || user.length < 2) return email

    const first = user[0]
    const last = user[user.length - 1]

    const middle = user.slice(1, -1)
    const middleLength = middle.length

    const maskedMiddle = middle
      .split('')
      .map((_, i) => '*')
      .map((_, i, arr) => {
        const positionsToTry = [
          0,
          Math.floor(middleLength / 2),
          middleLength - 1
        ]
        if (
          positionsToTry.includes(i) &&
          arr[i - 1] !== middle[i - 1] &&
          arr[i + 1] !== middle[i + 1]
        ) {
          return middle[i]
        }
        return '*'
      })

    const maskedUser = `${first}${maskedMiddle.join('')}${last}`
    const maskedDomain = `${domain[0]}${'*'.repeat(domain.length - 1)}`
    return `${maskedUser}@${maskedDomain}`
  }

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
      clearInterval(intervalRef.current)
    }
  }, [])

  useEffect(() => {
    solicitarCodigoInicial()

    inputsRef.current[0]?.focus()

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const iniciarContagem = (inicial = 300) => {
    setTimeLeft(inicial)
    setCanResend(false)
    clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current)
          setCanResend(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const solicitarCodigoInicial = async () => {
    setLoading(true)

    const enviadoEm = sessionStorage.getItem('codigoEnviadoEm')
    const agora = Date.now()
    const cincoMinutos = 5 * 60 * 1000

    if (enviadoEm && agora - enviadoEm < cincoMinutos) {
      const restante = Math.floor((cincoMinutos - (agora - enviadoEm)) / 1000)
      setTimeLeft(restante)
      setCanResend(false)
      iniciarContagem(restante)
      setLoading(false)
      return
    }

    const sucesso = await solicitarCodigo()
    if (sucesso) {
      sessionStorage.setItem('codigoEnviadoEm', Date.now().toString())
      iniciarContagem(300)
    }

    setLoading(false)
  }

  const handleChange = (value, idx) => {
    if (!/^[0-9]?$/.test(value)) return
    const newCode = [...code]
    newCode[idx] = value
    setCode(newCode)
    if (value && idx < 5) {
      inputsRef.current[idx + 1]?.focus()
    }
    if (newCode.every((v) => v !== '')) {
      verificarCodigo(newCode)
    }
  }

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const newCode = [...code]
      if (code[idx]) {
        newCode[idx] = ''
        setCode(newCode)
        inputsRef.current[idx].select()
      } else if (idx > 0) {
        newCode[idx - 1] = ''
        setCode(newCode)
        inputsRef.current[idx - 1]?.focus()
      }
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const paste = e.clipboardData.getData('text').replace(/\D/g, '')
    const digits = paste.slice(0, 6).split('')
    const newCode = ['', '', '', '', '', '']

    digits.forEach((d, i) => {
      newCode[i] = d
    })

    setCode(newCode)

    setTimeout(() => {
      if (digits.length === 6) {
        verificarCodigo(newCode)
      } else {
        inputsRef.current[digits.length]?.focus()
      }
    }, 0)
  }

  const solicitarCodigo = async () => {
    try {
      const response = await clientes.solicitarCodigoAutorizacaoEmail(novoEmail)

      if (response.ok) {
        return true
      } else {
        addToast({ type: 'error', title: 'Erro ao solicitar código!' })
        return false
      }
    } catch (err) {
      console.error('Erro ao solicitar código:', err)
      addToast({ type: 'error', title: 'Erro ao solicitar código' })
      return false
    }
  }

  const verificarCodigo = async (codigoArray) => {
    setStatus('checking')
    try {
      const codigo = codigoArray.join('')
      const response = await clientes.consultarCodigoAutorizacaoEmail(
        novoEmail,
        codigo
      )

      if (response.ok) {
        sessionStorage.removeItem('codigoEnviadoEm')
        setCodigoConfirmado(codigo)
        onConfirm(codigo)
      } else if (response.status === 400) {
        const data = await response.json().catch(() => ({}))
        const message = data?.errors?.Messages?.[0] || ''

        if (message.toLowerCase().includes('expirado')) {
          setStatus('expired')
          addToast({ type: 'error', title: 'Código expirado.' })
        } else if (message.toLowerCase().includes('inválido')) {
          setStatus('invalid')
          addToast({ type: 'error', title: 'Código inválido.' })
        } else {
          setStatus('error')
          addToast({
            type: 'error',
            title: 'Erro ao verificar código',
            description: 'Favor validar os dados.'
          })
        }
      } else {
        setStatus('error')
        addToast({
          type: 'error',
          title: 'Erro ao verificar código',
          description: 'Favor validar os dados.'
        })
      }
    } catch (error) {
      console.error('Erro ao verificar código', error)
      setStatus('error')
      addToast({
        type: 'error',
        title: 'Erro ao verificar código',
        description: 'Favor validar os dados.'
      })
    }
  }

  const resendCode = async () => {
    if (loading) return
    setLoading(true)
    setCode(['', '', '', '', '', ''])
    setStatus('idle')

    const sucesso = await solicitarCodigo()
    if (sucesso) {
      sessionStorage.setItem('codigoEnviadoEm', Date.now().toString())
      iniciarContagem(300)
      addToast({ type: 'success', title: 'Código reenviado' })
      inputsRef.current[0]?.focus()
    }

    setLoading(false)
  }

  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, '0')
    const s = String(sec % 60).padStart(2, '0')
    return `${m}:${s}`
  }

  return (
    <S.Background>
      <S.ModalWrapper>
        {loading && <Loader />}
        <S.HeaderModalClose onClick={onClose}>
          <img src={closeIcon} alt="Fechar" />
        </S.HeaderModalClose>
        <S.ModalContent>
          <S.Title>Confirme o código enviado via e-mail</S.Title>
          <S.Subtitle>
            Informe o código de confirmação que enviamos para{' '}
            {maskEmail(userEmail)}
          </S.Subtitle>

          <S.InputContainer onPaste={handlePaste}>
            {code.map((digit, i) => (
              <S.InputCode
                key={i}
                type="text"
                maxLength={1}
                value={digit}
                ref={(el) => (inputsRef.current[i] = el)}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onFocus={(e) => e.target.select()}
              />
            ))}
          </S.InputContainer>

          {status === 'checking' && (
            <S.StatusMsg>Verificando código...</S.StatusMsg>
          )}
          {status === 'expired' && (
            <S.ErrorMsg>Código expirado. Solicite novo código.</S.ErrorMsg>
          )}
          {status === 'invalid' && <S.ErrorMsg>Código Inválido.</S.ErrorMsg>}

          <S.ResendMsg>
            {!canResend ? (
              <>
                Você poderá solicitar um novo código em {formatTime(timeLeft)}
              </>
            ) : (
              <>
                Para reenviar o código,{' '}
                <S.ResendLink onClick={resendCode}>clique aqui</S.ResendLink>
              </>
            )}
          </S.ResendMsg>
        </S.ModalContent>
      </S.ModalWrapper>
    </S.Background>
  )
}

export default ModalConfirmacaoCode
